"use client";

import { useEffect, useRef, useState } from "react";
import { Base64 } from "js-base64";
import { PortableText } from "next-sanity";
import { createPortableTextComponents } from "@/utils/portableTextComponents";
import { getStoredUtms } from "@/utils/utm";
import { t } from "@/utils/t";
import { IsovContent } from "@/components/stencil";

const HUBSPOT_PORTAL_ID = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

const HS_SUBMIT_RE =
  /^https:\/\/forms\.hsforms\.com\/submissions\/v3\/public\/submit\/formsnext\/multipart(\/.*)?$/;
const PROXY_PREFIX = "/api/hsforms/submit";

const IS_FIREFOX =
  typeof navigator !== "undefined" && /Firefox\//i.test(navigator.userAgent);

function proxyHsUrl(url: string): string {
  if (!HS_SUBMIT_RE.test(url)) return url;
  const proxied = url.replace(
    /^https:\/\/forms\.hsforms\.com\/submissions\/v3\/public\/submit\/formsnext\/multipart/,
    PROXY_PREFIX,
  );
  return proxied.endsWith("/") ? proxied : `${proxied}/`;
}

let firefoxInterceptInstalled = false;

function installFirefoxIntercept(): void {
  if (firefoxInterceptInstalled) return;
  if (!IS_FIREFOX) return;
  firefoxInterceptInstalled = true;

  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null,
  ) {
    return origOpen.call(
      this,
      method,
      proxyHsUrl(String(url)),
      async,
      username,
      password,
    );
  } as typeof XMLHttpRequest.prototype.open;

  const origFetch = window.fetch;
  window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
    const urlStr =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;

    if (HS_SUBMIT_RE.test(urlStr)) {
      const proxied = proxyHsUrl(urlStr);
      return origFetch(
        typeof input === "string" ? proxied : new URL(proxied),
        init,
      );
    }
    return origFetch(input, init);
  };
}

const HIDDEN_FIELD_PAGE_NAME = "conversion_page_name";
const HIDDEN_FIELD_PAGE_URL = "conversion_page_url";
const HIDDEN_FIELD_EVENT_DATES = "event_dates";
const UTM_FIELD_MAP: Record<string, string> = {
  utm_source: "utm_source",
  utm_medium: "utm_medium",
  utm_campaign: "utm_campaign",
  utm_term: "utm_term",
  utm_content: "utm_content",
};

interface HubSpotFormProps {
  formId: string;
  webexMeetingId?: string;
  redirectUrl?: string;
  thankYouMessage?: any;
  eventDates?: string;
  minHeight?: number;
  onFormReady?: () => void;
  onFormSubmit?: (data: any) => void;
  hasRedirect?: boolean;
  theme?: string;
  locale?: string;
  hiddenFields?: Record<string, string>;
}

export default function HubSpotForm({
  formId = "c8155cb1-aff6-429c-a5ad-fb3cf9e0a81d",
  onFormSubmit,
  onFormReady,
  hasRedirect = false,
  theme,
  locale = "en",
  webexMeetingId,
  redirectUrl,
  thankYouMessage,
  eventDates,
  minHeight = 132,
  hiddenFields,
}: HubSpotFormProps) {
  const ptComponents = createPortableTextComponents(locale);
  const localizedThankYouMessage = t(thankYouMessage, locale);
  const resolvedThankYouMessage = localizedThankYouMessage?.length
    ? localizedThankYouMessage
    : undefined;
  const [submitted, setSubmitted] = useState(false);
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const thankYouMessageRef = useRef(resolvedThankYouMessage);
  const hasRedirectRef = useRef(hasRedirect);
  const redirectUrlRef = useRef(redirectUrl);
  const setSubmittedRef = useRef(setSubmitted);
  const onFormSubmitRef = useRef(onFormSubmit);
  const onFormReadyRef = useRef(onFormReady);
  const hiddenFieldsRef = useRef(hiddenFields);
  const formCreatedRef = useRef(false);

  useEffect(() => {
    thankYouMessageRef.current = resolvedThankYouMessage;
    hasRedirectRef.current = hasRedirect;
    redirectUrlRef.current = redirectUrl;
    onFormSubmitRef.current = onFormSubmit;
    onFormReadyRef.current = onFormReady;
    hiddenFieldsRef.current = hiddenFields;
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;

    installFirefoxIntercept();
    formCreatedRef.current = false;

    const LABS_FORM_ID = "dbd53215-c51f-4627-a403-eceaba3a7b42";
    const LABS_SLUG_FIELD = "last_lab_signup";
    const WEBINAR_ID_FIELD = "webex_webinar_id";

    const getDeviceType = (): string => {
      const ua = navigator.userAgent;
      if (/Mobi|Android/i.test(ua)) return "mobile";
      if (/Tablet|iPad/i.test(ua)) return "tablet";
      return "desktop";
    };

    const getPageSlug = (): string => {
      try {
        const path = window.location.pathname.replace(/\/$/, "");
        const parts = path.split("/").filter(Boolean);
        return parts[parts.length - 1] || "home";
      } catch {
        return "";
      }
    };

    const getCookie = (name: string): string | undefined => {
      if (typeof document === "undefined") return;
      const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
      return m?.[2];
    };

    const ensureHiddenField = (
      formEl: HTMLFormElement,
      name: string,
      value: string,
      allowOverwriteHubSpotOwned: boolean,
    ): void => {
      let input = formEl.querySelector(
        `input[name="${name}"]`,
      ) as HTMLInputElement | null;

      const ownedByHubSpot =
        Boolean(input) && input?.dataset.isovHidden !== "true";

      // Never touch a field HubSpot itself defines until it's safe to -
      // right before an actual submit. Writing to it during onFormReady
      // races HubSpot's own field/validation setup, which is exactly when
      // a visible field can appear to silently reject typed input.
      if (ownedByHubSpot && !allowOverwriteHubSpotOwned) return;

      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.dataset.isovHidden = "true";
        formEl.appendChild(input);
      }

      if (input.value !== value) {
        input.value = value;
        if (!ownedByHubSpot) {
          try {
            input.dispatchEvent(new Event("change", { bubbles: true }));
          } catch {}
        }
      }
    };

    const getFormEl = ($form: any): HTMLFormElement | null =>
      $form && typeof $form.get === "function" ? $form.get(0) : $form;

    const populateHiddenFields = (
      $form: any,
      allowOverwriteHubSpotOwned: boolean,
    ): void => {
      const formEl = getFormEl($form);
      if (!formEl) return;

      try {
        const pageName = document.title || window.location.pathname;
        ensureHiddenField(
          formEl,
          HIDDEN_FIELD_PAGE_NAME,
          pageName,
          allowOverwriteHubSpotOwned,
        );
        ensureHiddenField(
          formEl,
          HIDDEN_FIELD_PAGE_URL,
          window.location.href,
          allowOverwriteHubSpotOwned,
        );

        if (eventDates) {
          ensureHiddenField(
            formEl,
            HIDDEN_FIELD_EVENT_DATES,
            eventDates,
            allowOverwriteHubSpotOwned,
          );
        }

        const utms = getStoredUtms();
        Object.entries(UTM_FIELD_MAP).forEach(([utmKey, fieldName]) => {
          const value = utms[utmKey as keyof typeof utms];
          if (value) {
            ensureHiddenField(
              formEl,
              fieldName,
              value,
              allowOverwriteHubSpotOwned,
            );
          }
        });

        if (formId === LABS_FORM_ID) {
          ensureHiddenField(
            formEl,
            LABS_SLUG_FIELD,
            getPageSlug(),
            allowOverwriteHubSpotOwned,
          );
        }

        if (webexMeetingId) {
          ensureHiddenField(
            formEl,
            WEBINAR_ID_FIELD,
            webexMeetingId.replace(/\s+/g, ""),
            allowOverwriteHubSpotOwned,
          );
        }

        if (hiddenFieldsRef.current) {
          Object.entries(hiddenFieldsRef.current).forEach(([name, value]) => {
            if (value)
              ensureHiddenField(
                formEl,
                name,
                value,
                allowOverwriteHubSpotOwned,
              );
          });
        }
      } catch {}
    };

    const collectFormValues = (): Record<string, string> => {
      const data: Record<string, string> = {};
      try {
        const formEl = document.querySelector(
          `#hubspot-form-${formId} form`,
        ) as HTMLFormElement | null;

        if (!formEl) return data;

        const inputs = formEl.querySelectorAll<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >("input[name], select[name], textarea[name]");

        inputs.forEach((input) => {
          if (!input.name) return;

          if (input instanceof HTMLInputElement) {
            if (input.type === "checkbox") {
              data[input.name] = String(input.checked);
              return;
            }
            if (input.type === "radio") {
              if (input.checked) data[input.name] = input.value;
              return;
            }
          }

          data[input.name] = input.value;
        });
      } catch (err) {
        console.warn("collectFormValues failed", err);
      }
      return data;
    };

    const registerConference = async (payload: {
      email?: string;
      firstname?: string;
      lastname?: string;
      formId: string;
      webexMeetingId?: string;
      values: Record<string, string>;
      hutk?: string;
      pageUri?: string;
      pageName?: string;
      xRequestId?: string;
    }) => {
      const url = "/api/webex/register/";
      const body = JSON.stringify(payload);

      if (
        webexMeetingId &&
        typeof navigator !== "undefined" &&
        "sendBeacon" in navigator &&
        (hasRedirectRef.current || redirectUrlRef.current)
      ) {
        try {
          const blob = new Blob([body], { type: "application/json" });
          (navigator as any).sendBeacon(url, blob);
          return;
        } catch {}
      }

      try {
        await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        });
      } catch {
        // Continue...
      }
    };

    const getRecaptchaToken = (): Promise<string> =>
      new Promise((resolve) => {
        try {
          (window as any).grecaptcha.ready(() => {
            (window as any).grecaptcha
              .execute(RECAPTCHA_SITE_KEY, { action: "labs_redirect" })
              .then((token: string) => resolve(token))
              .catch(() => resolve(""));
          });
        } catch {
          resolve("");
        }
      });

    const redirectToLab = async () => {
      if (typeof document !== "undefined") {
        const redirectUrlValue = `https://proxy.labs-funcs.isovalent.tech/lab?slug=${getPageSlug()}`;
        const email = Base64.encode(
          (
            document.querySelector(
              'input[name="email"]',
            ) as HTMLInputElement | null
          )?.value || "",
        );
        const firstname = Base64.encode(
          (
            document.querySelector(
              'input[name="firstname"]',
            ) as HTMLInputElement | null
          )?.value || "",
        );
        const lastname = Base64.encode(
          (
            document.querySelector(
              'input[name="lastname"]',
            ) as HTMLInputElement | null
          )?.value || "",
        );

        const recaptchaToken = await getRecaptchaToken();

        const redirectUrlParams = new URLSearchParams({
          icp_a: email,
          icp_fn: firstname,
          icp_ln: lastname,
          icp_source: "website",
          icp_device: getDeviceType(),
          icp_screen_size: `${window.screen.width}x${window.screen.height}`,
        });

        if (recaptchaToken)
          redirectUrlParams.append("recaptcha_token", recaptchaToken);

        const currentParams = new URLSearchParams(window.location.search);
        currentParams.forEach((value, key) => {
          if (key.startsWith("utm_")) {
            redirectUrlParams.append(key, value);
          }
        });

        const separator = redirectUrlValue.includes("?") ? "&" : "?";
        // Absolute external URL (the labs proxy), which the Next router can't
        // navigate to. The rule can't tell, as the string starts with a variable.
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination
        window.location.href = `${redirectUrlValue}${separator}${redirectUrlParams.toString()}`;
      }
    };

    const loadHubSpotForm = () => {
      if (!(window as any).hbspt) return;
      if (formCreatedRef.current) return;
      formCreatedRef.current = true;

      (window as any).hbspt.forms.create({
        portalId: HUBSPOT_PORTAL_ID,
        formId: formId,
        target: `#hubspot-form-${formId}-embed`,
        onFormReady: ($form: any) => {
          populateHiddenFields($form, false);
          if (onFormReadyRef.current) onFormReadyRef.current();
        },
        onBeforeFormSubmit: ($form: any) => {
          populateHiddenFields($form, true);
        },
        onFormSubmit: async (_$form: any) => {
          document
            .getElementById(`hubspot-form-${formId}`)
            ?.scrollIntoView({ behavior: "smooth" });
          const values = collectFormValues();
          if (onFormSubmitRef.current) onFormSubmitRef.current(values);

          if (webexMeetingId) {
            const cleanedWebexId = webexMeetingId.replace(/\s+/g, "");
            const email = values.email || values.email_address || "";
            const firstname = values.firstname || values.firstName || "";
            const lastname = values.lastname || values.lastName || "";

            const xRequestId = `${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 8)}`;

            await registerConference({
              email,
              firstname,
              lastname,
              formId,
              webexMeetingId: cleanedWebexId,
              values,
              hutk: getCookie("hubspotutk"),
              pageUri:
                typeof window !== "undefined"
                  ? window.location.href
                  : undefined,
              pageName:
                typeof document !== "undefined" ? document.title : undefined,
              xRequestId,
            });
          }

          if (hasRedirectRef.current) await redirectToLab();
        },
        onFormSubmitted: () => {
          if (hasRedirectRef.current) return;
          if (redirectUrlRef.current) {
            window.location.href = redirectUrlRef.current;
            return;
          }
          if (thankYouMessageRef.current) setSubmittedRef.current(true);
        },
      });
    };

    const loadScript = (src: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => ((window as any).hbspt ? resolve() : reject());
        script.onerror = reject;
        document.body.appendChild(script);
      });

    const loadRecaptchaScript = (): Promise<void> =>
      new Promise((resolve) => {
        if (
          !RECAPTCHA_SITE_KEY ||
          document.getElementById("recaptcha-v3-script")
        ) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.id = "recaptcha-v3-script";
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.body.appendChild(script);
      });

    let mounted = true;

    (async () => {
      if (!(window as any).hbspt) {
        try {
          await loadScript("https://js.hsforms.net/forms/v2.js");
        } catch {
          try {
            await loadScript("/api/hsforms/");
          } catch {
            console.warn("Failed to load HubSpot forms from both sources.");
            return;
          }
        }
      }
      if (!mounted) return;
      await loadRecaptchaScript();
      if (!mounted) return;
      loadHubSpotForm();
    })();

    return () => {
      mounted = false;
    };
  }, [visible, formId, webexMeetingId, eventDates]);

  const showThankYou = submitted && resolvedThankYouMessage;

  return (
    <div
      ref={containerRef}
      id={`hubspot-form-${formId}`}
      className={`hubspot-form-container theme-${theme}`}
    >
      {showThankYou && (
        <IsovContent theme={theme}>
          <PortableText
            value={resolvedThankYouMessage}
            components={ptComponents}
            onMissingComponent={() => {}}
          />
        </IsovContent>
      )}
      <div
        id={`hubspot-form-${formId}-embed`}
        style={{
          minHeight: `${minHeight}px`,
          display: showThankYou ? "none" : undefined,
        }}
      />
    </div>
  );
}
